import { api, type Id } from "@repo/db"
import { DocumentUploadModal, TemplateList } from "@repo/docs"
import { Button } from "@repo/shad/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/shad/ui/dialog"
import { Input } from "@repo/shad/ui/input"
import { Label } from "@repo/shad/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/shad/ui/select"
import { Skeleton } from "@repo/shad/ui/skeleton"
import { Textarea } from "@repo/shad/ui/textarea"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { ChevronRight, FileText, LayoutTemplate } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { DocsFeatureDisabledState } from "@/components/docs/docs-feature-disabled-state"
import { useDocsFeatureEnabled } from "@/features/docs/feature-gate"

export const Route = createFileRoute("/$dealership/deal-packets/templates/")({
  component: TemplatesPage,
})

function TemplatesPage() {
  const navigate = useNavigate()
  const context = Route.useRouteContext()
  const [templateChooserOpen, setTemplateChooserOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [webTemplateDialogOpen, setWebTemplateDialogOpen] = useState(false)
  const [isCreatingWebTemplate, setIsCreatingWebTemplate] = useState(false)
  const [webTemplateName, setWebTemplateName] = useState("")
  const [webTemplateDescription, setWebTemplateDescription] = useState("")
  const [webTemplateCategory, setWebTemplateCategory] = useState<
    "sales" | "finance" | "service" | "trade-in" | "other"
  >("sales")

  const dealership = context && "dealership" in context ? context.dealership : null
  const user = context && "user" in context ? context.user : null
  const { enabled: docsEnabled, isPending: docsFeaturePending } = useDocsFeatureEnabled(
    dealership?._id,
  )
  const createWebTemplate = useMutation(api.documents.mutations.createWebTemplate)

  const handleSelectTemplate = (templateId: string) => {
    void navigate({
      to: "/$dealership/deal-packets/templates/$templateId",
      params: { dealership: dealership?.slug ?? "", templateId },
    })
  }

  const handleUploadClick = () => {
    setTemplateChooserOpen(false)
    setUploadModalOpen(true)
  }

  const handleOpenDocumentRequestDialog = () => {
    setTemplateChooserOpen(false)
    setWebTemplateDialogOpen(true)
  }

  const handleCreateWebTemplate = async () => {
    if (!webTemplateName.trim()) {
      toast.error("Document request name is required")
      return
    }

    setIsCreatingWebTemplate(true)
    try {
      const result = await createWebTemplate({
        name: webTemplateName.trim(),
        description: webTemplateDescription.trim() || undefined,
        category: webTemplateCategory,
        compliance: {
          esignAllowed: true,
          identityRequirement: "none",
        },
      })
      setWebTemplateDialogOpen(false)
      setWebTemplateName("")
      setWebTemplateDescription("")
      setWebTemplateCategory("sales")
      toast.success("Document request created", {
        description: "Add instructions, dealership values, and extra questions next.",
      })
      void navigate({
        to: "/$dealership/deal-packets/templates/$templateId",
        params: {
          dealership: dealership?.slug ?? "",
          templateId: result.templateId,
        },
      })
    } catch (error) {
      toast.error("Failed to create document request", {
        description: error instanceof Error ? error.message : "An error occurred",
      })
    } finally {
      setIsCreatingWebTemplate(false)
    }
  }

  const handleUploadComplete = (templateId: Id<"documentTemplates">) => {
    setUploadModalOpen(false)
    toast.success("Template created", {
      description: "Map fields to finish configuring the template.",
    })

    void navigate({
      to: "/$dealership/deal-packets/templates/$templateId/fields",
      params: {
        dealership: dealership?.slug ?? "",
        templateId,
      },
    })
  }

  if (!dealership || !user?.userId) {
    return <TemplatesPageSkeleton />
  }

  if (docsFeaturePending) {
    return <TemplatesPageSkeleton />
  }

  if (!docsEnabled) {
    return (
      <DocsFeatureDisabledState
        title="Template management is turned off"
        description="The docs workflow is currently disabled for this dealership. Enable dealSigningDocs to upload templates, map fields, and build deal forms."
      />
    )
  }

  return (
    <div>
      <TemplateList
        onSelectTemplate={handleSelectTemplate}
        action={
          <Button onClick={() => setTemplateChooserOpen(true)}>
            <LayoutTemplate className="mr-2 h-4 w-4" />
            New Template
          </Button>
        }
      />

      <DocumentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUploadComplete={handleUploadComplete}
        maxSizeMB={10}
        dealershipId={dealership._id}
        userId={user.userId}
      />

      <Dialog open={templateChooserOpen} onOpenChange={setTemplateChooserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Template</DialogTitle>
            <DialogDescription>Choose how to create your template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleUploadClick}
              className="flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/50"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FileText className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">PDF Template</p>
                <p className="text-xs text-muted-foreground">
                  Upload a PDF and map fields for signing.
                </p>
              </div>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </button>
            <button
              type="button"
              onClick={handleOpenDocumentRequestDialog}
              className="flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/50"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <LayoutTemplate className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Deal Form</p>
                <p className="text-xs text-muted-foreground">
                  Build a dealership-branded questionnaire with optional file uploads.
                </p>
              </div>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={webTemplateDialogOpen} onOpenChange={setWebTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Deal Form</DialogTitle>
            <DialogDescription>
              Give the form a name and category. You can add questions, inline deal data, and
              optional upload requests after creating it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="web-template-name">Name</Label>
              <Input
                id="web-template-name"
                value={webTemplateName}
                onChange={(event) => setWebTemplateName(event.target.value)}
                placeholder="Driver's license"
                disabled={isCreatingWebTemplate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="web-template-category">Category</Label>
              <Select
                value={webTemplateCategory}
                onValueChange={(value) =>
                  setWebTemplateCategory(
                    value as "sales" | "finance" | "service" | "trade-in" | "other",
                  )
                }
                disabled={isCreatingWebTemplate}
              >
                <SelectTrigger id="web-template-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="trade-in">Trade-In</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="web-template-description">Description</Label>
              <Textarea
                id="web-template-description"
                rows={3}
                value={webTemplateDescription}
                onChange={(event) => setWebTemplateDescription(event.target.value)}
                placeholder="Any additional instructions or context for this request."
                disabled={isCreatingWebTemplate}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWebTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateWebTemplate()} disabled={isCreatingWebTemplate}>
              {isCreatingWebTemplate ? "Creating..." : "Create Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function TemplatesPageSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-64" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-[180px]" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-4 space-y-3">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <div className="flex justify-between pt-1">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
